import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { ActualPack, findVirtualBrands, Query } from '~/Api/Analytics/VirtualBrand';
import { fetchAnalyticsOfVirtualBrand, fetchVirtualBrand } from '~/Api/Analytics/VirtualBrand/findVirtualBrands';
import { retryFetchAfterUserProductGroupLock, UserProductGroup } from '~/Api/User/ProductGroup';
import { addProductsToUserGroup } from '~/Api/User/ProductGroup/Product';
import { invokeApi, resolve, tryResolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { ThunkAction } from '~/Store';

import ActionTypes from './ActionTypes';
import { type RequestAnalyticsOfVirtualBrand, selectVirtualBrands } from './reducer';
import { WrappedExportResponse } from 'com.cogvio.pricemonitor.api';
import { createAnalyticsExport, ExportAnalyticsRequest } from '~/Api/Analytics/VirtualBrand/exportVirtualBrands';
import FilterParameter from '~/libs/FilterParameter/FilterParameter';

export function fetchVirtualBrands(query: Query, page: number, perPage = 10): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const request = dispatch(invokeApi(findVirtualBrands, query, page, perPage));
		dispatch({ type: ActionTypes.request, request, query });

		await tryResolve(request, (response, error) => {
			dispatch({
				type: ActionTypes.receive,
				request,
				query,
				items: response?.items,
				pagination: response?.pagination,
			});
			if (error) {
				dispatch(handleError(error));
			}
		});
	};
}
export function getVirtualBrand(virtualBrandId: string): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const request = dispatch(invokeApi(fetchVirtualBrand, virtualBrandId));
		dispatch({ type: ActionTypes.request, request });

		await tryResolve(request, (response, error) => {
			dispatch({
				type: ActionTypes.add,
				item: response,
			});
			if (error) {
				dispatch(handleError(error));
			}
		});
	};
}

export function fetchVirtualBrandAnalytics(virtualBrandId: string): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const request = dispatch(invokeApi(fetchAnalyticsOfVirtualBrand, virtualBrandId));

		dispatch<RequestAnalyticsOfVirtualBrand>({
			type: ActionTypes.requestAnalyticsOfVirtualBrand,
			brandId: virtualBrandId,
		});

		await tryResolve(request, (analyticVirtualBrand, error) => {
			dispatch({
				type: ActionTypes.addAnalyticsOfVirtualBrand,
				analyticVirtualBrand,
			});
			if (error) {
				dispatch(handleError(error));
			}
		});
	};
}

export function fetchMoreVirtualBrands(query: Query): ThunkAction<Promise<void>, any> {
	return async (dispatch, getState) => {
		const virtualBrandsState = selectVirtualBrands(getState().medicinalBrandsAnalyticsVirtualBrand, query);
		if (!virtualBrandsState || !virtualBrandsState.pagination) {
			await dispatch(fetchVirtualBrands(query, 1));
			return;
		}

		const { pagination } = virtualBrandsState;
		const nextPage = pagination.getNextPage();
		if (nextPage) {
			await dispatch(fetchVirtualBrands(query, nextPage, pagination.perPage));
		}
	};
}

export function exportVirtualBrands(query: Query): ThunkAction<Promise<WrappedExportResponse | null>, any> {
	return async (dispatch) => {
		const data: ExportAnalyticsRequest = {
			brandNames: [],
			atcs: [],
			filterTherapeuticAreas: [],
			currency: query.currency,
		};

		query.filterParameters.map((filterParameter: FilterParameter<string | string[]>) => {
			if (filterParameter.name === 'brand') {
				data.brandNames = filterParameter.value as string[];
			} else if (filterParameter.name === 'atc') {
				data.atcs = filterParameter.value as string[];
			} else if (filterParameter.name === 'therapeutic-area') {
				data.filterTherapeuticAreas = filterParameter.value as string[];
			}
		});

		const request = dispatch(invokeApi(createAnalyticsExport, data));

		dispatch({ type: ActionTypes.requestAnalyticsExport, request });

		try {
			const response = await request;
			dispatch({ type: ActionTypes.receiveAnalyticsExport, request });
			return response;
		} catch (error) {
			dispatch({ type: ActionTypes.receiveAnalyticsExport, request });
			dispatch(handleError(error));
		}

		return null;
	};
}

export function addGroupActualPacks(
	group: UserProductGroup,
	actualPacks: ActualPack[]
): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(
				invokeApi(
					addProductsToUserGroup,
					userId,
					group.id,
					actualPacks.map((actualPack) => ({
						id: actualPack.id,
						packageSizeForSingleUnit: actualPack.packageSizeForSingleUnit,
					})),
					null
				)
			)
		);

		dispatch({ type: ActionTypes.requestAddGroupActualPacks, request });
		await resolve(request, () => dispatch({ type: ActionTypes.receiveAddGroupActualPacks, request }));
	};
}
